import React, { useEffect, useMemo, useState } from "react";
import { consumeNavPath } from "@/lib/adminNav";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tag, Plus, Trash2, Edit, RefreshCw, Layers } from "lucide-react";

type EcardTag = {
  id: string;
  name: string;
  description: string | null;
  color: string;
  applies_to_card: boolean;
  applies_to_promotion: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type Promo = {
  id: string;
  promo_name: string;
  start_date: string;
  end_date: string;
  is_active: boolean | string;
};

type ECard = {
  id: string;
  card_last4: string;
  card_number: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  status: string;
};

const COLOR_PRESETS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16",
  "#f97316", "#6366f1",
];

export default function AdminECardTagsPage() {
  const [activeTab, setActiveTab] = useState("manage");

  // Deep-link from the Site Map (Settings -> Site Map) -- see adminNav.ts.
  // path[0] ("tags") is consumed by AdminECardHub.tsx; this reads path[1].
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[1]) setActiveTab(path[1]);
  }, []);
  const [tags, setTags] = useState<EcardTag[]>([]);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [cards, setCards] = useState<ECard[]>([]);
  const [tagAssignments, setTagAssignments] = useState<{
    cards: Record<string, string[]>; // tag_id -> ecard_ids
    promos: Record<string, string[]>; // tag_id -> promo_ids
    topups: Record<string, number>; // tag_id -> topup count
  }>({ cards: {}, promos: {}, topups: {} });

  const [loading, setLoading] = useState(false);

  // Tag CRUD modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<EcardTag | null>(null);
  const [tagName, setTagName] = useState("");
  const [tagDesc, setTagDesc] = useState("");
  const [tagColor, setTagColor] = useState("#3b82f6");
  const [appliesToCard, setAppliesToCard] = useState(true);
  const [appliesToPromo, setAppliesToPromo] = useState(true);
  const [tagActive, setTagActive] = useState(true);

  // Assign modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTag, setAssignTag] = useState<EcardTag | null>(null);
  const [assignTargetType, setAssignTargetType] = useState<"card" | "promo">("promo");
  const [selectedTargetIds, setSelectedTargetIds] = useState<Set<string>>(new Set());
  const [searchAssign, setSearchAssign] = useState("");

  // Backfill modal state
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillTag, setBackfillTag] = useState<EcardTag | null>(null);
  const [backfillPromoId, setBackfillPromoId] = useState<string>("");
  const [backfillCount, setBackfillCount] = useState<number | null>(null);
  const [backfilling, setBackfilling] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const [tagsRes, promosRes, cardsRes, ctRes, ptRes, ttRes] = await Promise.all([
        supabase.from("ecard_tags").select("*").order("name"),
        supabase.from("ecard_promotions").select("id, promo_name, start_date, end_date, is_active").order("start_date", { ascending: false }),
        supabase.from("ecards").select("id, card_last4, card_number, customer_first_name, customer_last_name, status").order("card_last4"),
        supabase.from("ecard_card_tags").select("ecard_id, tag_id"),
        supabase.from("ecard_promo_tags").select("promo_id, tag_id"),
        supabase.from("ecard_topup_tags").select("tag_id"),
      ]);

      if (tagsRes.error) throw tagsRes.error;
      if (promosRes.error) throw promosRes.error;
      if (cardsRes.error) throw cardsRes.error;

      setTags((tagsRes.data ?? []) as EcardTag[]);
      setPromos((promosRes.data ?? []) as Promo[]);
      setCards((cardsRes.data ?? []) as ECard[]);

      const cardMap: Record<string, string[]> = {};
      const promoMap: Record<string, string[]> = {};
      const topupMap: Record<string, number> = {};
      for (const r of (ctRes.data ?? []) as Array<{ ecard_id: string; tag_id: string }>) {
        if (!cardMap[r.tag_id]) cardMap[r.tag_id] = [];
        cardMap[r.tag_id].push(r.ecard_id);
      }
      for (const r of (ptRes.data ?? []) as Array<{ promo_id: string; tag_id: string }>) {
        if (!promoMap[r.tag_id]) promoMap[r.tag_id] = [];
        promoMap[r.tag_id].push(r.promo_id);
      }
      for (const r of (ttRes.data ?? []) as Array<{ tag_id: string }>) {
        topupMap[r.tag_id] = (topupMap[r.tag_id] ?? 0) + 1;
      }
      setTagAssignments({ cards: cardMap, promos: promoMap, topups: topupMap });
    } catch (e: any) {
      toast({ title: "Failed to load tags", description: e?.message ?? String(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  function openNew() {
    setEditingTag(null);
    setTagName("");
    setTagDesc("");
    setTagColor("#3b82f6");
    setAppliesToCard(true);
    setAppliesToPromo(true);
    setTagActive(true);
    setModalOpen(true);
  }

  function openEdit(t: EcardTag) {
    setEditingTag(t);
    setTagName(t.name);
    setTagDesc(t.description ?? "");
    setTagColor(t.color || "#3b82f6");
    setAppliesToCard(t.applies_to_card);
    setAppliesToPromo(t.applies_to_promotion);
    setTagActive(t.is_active);
    setModalOpen(true);
  }

  async function saveTag() {
    if (!tagName.trim()) {
      toast({ title: "Name required" });
      return;
    }
    if (!appliesToCard && !appliesToPromo) {
      toast({ title: "Select at least one", description: "Tag must apply to cards or promotions" });
      return;
    }

    const payload = {
      name: tagName.trim(),
      description: tagDesc.trim() || null,
      color: tagColor,
      applies_to_card: appliesToCard,
      applies_to_promotion: appliesToPromo,
      is_active: tagActive,
      updated_at: new Date().toISOString(),
    };

    if (editingTag) {
      const { error } = await supabase.from("ecard_tags").update(payload).eq("id", editingTag.id);
      if (error) {
        toast({ title: "Update failed", description: error.message });
        return;
      }
      toast({ title: "Tag updated" });
    } else {
      const { error } = await supabase.from("ecard_tags").insert(payload);
      if (error) {
        toast({ title: "Create failed", description: error.message });
        return;
      }
      toast({ title: "Tag created" });
    }
    setModalOpen(false);
    await loadAll();
  }

  async function deleteTag(t: EcardTag) {
    if (!confirm(`Delete tag "${t.name}"? This will remove it from all cards, promotions, and top-ups.`)) return;
    const { error } = await supabase.from("ecard_tags").delete().eq("id", t.id);
    if (error) {
      toast({ title: "Delete failed", description: error.message });
      return;
    }
    toast({ title: "Tag deleted" });
    await loadAll();
  }

  function openAssign(t: EcardTag, type: "card" | "promo") {
    setAssignTag(t);
    setAssignTargetType(type);
    const existing = type === "card"
      ? tagAssignments.cards[t.id] ?? []
      : tagAssignments.promos[t.id] ?? [];
    setSelectedTargetIds(new Set(existing));
    setSearchAssign("");
    setAssignModalOpen(true);
  }

  async function saveAssignments() {
    if (!assignTag) return;
    const tagId = assignTag.id;
    const table = assignTargetType === "card" ? "ecard_card_tags" : "ecard_promo_tags";
    const fkCol = assignTargetType === "card" ? "ecard_id" : "promo_id";

    // Delete existing for this tag
    const delRes = await supabase.from(table).delete().eq("tag_id", tagId);
    if (delRes.error) {
      toast({ title: "Save failed", description: delRes.error.message });
      return;
    }

    // Insert new
    const ids = Array.from(selectedTargetIds);
    if (ids.length > 0) {
      const rows = ids.map((id) => ({ tag_id: tagId, [fkCol]: id }));
      const insRes = await supabase.from(table).insert(rows);
      if (insRes.error) {
        toast({ title: "Save failed", description: insRes.error.message });
        return;
      }
    }
    toast({ title: `${assignTargetType === "card" ? "Cards" : "Promotions"} updated`, description: `${ids.length} assigned to "${assignTag.name}"` });
    setAssignModalOpen(false);
    await loadAll();
  }

  function openBackfill(t: EcardTag) {
    setBackfillTag(t);
    // Default to first promo this tag is on
    const promoIds = tagAssignments.promos[t.id] ?? [];
    setBackfillPromoId(promoIds[0] ?? "");
    setBackfillCount(null);
    setBackfillOpen(true);
  }

  async function previewBackfill() {
    if (!backfillTag || !backfillPromoId) return;
    const { data, error, count } = await supabase
      .from("ecard_topups")
      .select("id", { count: "exact", head: true })
      .eq("promo_id", backfillPromoId);
    if (error) {
      toast({ title: "Preview failed", description: error.message });
      return;
    }
    setBackfillCount(count ?? 0);
  }

  async function runBackfill() {
    if (!backfillTag || !backfillPromoId) return;
    setBackfilling(true);
    try {
      // Get all topup ids for that promo
      const tRes = await supabase
        .from("ecard_topups")
        .select("id")
        .eq("promo_id", backfillPromoId);
      if (tRes.error) throw tRes.error;
      const topupIds = (tRes.data ?? []).map((r: { id: string }) => r.id);
      if (topupIds.length === 0) {
        toast({ title: "No top-ups", description: "No top-ups found for that promo." });
        setBackfilling(false);
        return;
      }

      // Find existing tag-topup links to avoid duplicates
      const existRes = await supabase
        .from("ecard_topup_tags")
        .select("topup_id")
        .eq("tag_id", backfillTag.id)
        .in("topup_id", topupIds);
      if (existRes.error) throw existRes.error;
      const existing = new Set((existRes.data ?? []).map((r: { topup_id: string }) => r.topup_id));

      const toInsert = topupIds
        .filter((id) => !existing.has(id))
        .map((id) => ({ tag_id: backfillTag.id, topup_id: id }));

      if (toInsert.length === 0) {
        toast({ title: "Already up-to-date", description: "All matching top-ups already tagged." });
        setBackfilling(false);
        return;
      }

      // Insert in batches of 500
      for (let i = 0; i < toInsert.length; i += 500) {
        const batch = toInsert.slice(i, i + 500);
        const ins = await supabase.from("ecard_topup_tags").insert(batch);
        if (ins.error) throw ins.error;
      }

      toast({ title: "Backfill complete", description: `Tagged ${toInsert.length} top-up(s) with "${backfillTag.name}"` });
      setBackfillOpen(false);
      await loadAll();
    } catch (e: any) {
      toast({ title: "Backfill failed", description: e?.message ?? String(e) });
    } finally {
      setBackfilling(false);
    }
  }

  const filteredTargets = useMemo(() => {
    const q = searchAssign.trim().toLowerCase();
    if (assignTargetType === "promo") {
      return promos.filter((p) => !q || p.promo_name.toLowerCase().includes(q));
    }
    return cards.filter((c) => {
      if (!q) return true;
      const name = `${c.customer_first_name ?? ""} ${c.customer_last_name ?? ""}`.toLowerCase();
      return c.card_last4?.includes(q) || c.card_number?.includes(q) || name.includes(q);
    });
  }, [assignTargetType, promos, cards, searchAssign]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            eCard Tags
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAll} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={openNew}>
              <Plus className="h-4 w-4 mr-1" />
              New Tag
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Create custom tags to track where cards were sold or which promotion top-ups occurred under.
          Assign tags to cards and/or promotions, and backfill tags onto historical top-ups for any promo.
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="manage">Manage Tags</TabsTrigger>
          <TabsTrigger value="assign">Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="manage" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">All Tags ({tags.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tag</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Applies To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Cards</TableHead>
                      <TableHead className="text-right">Promos</TableHead>
                      <TableHead className="text-right">Top-ups</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tags.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>
                          <Badge style={{ backgroundColor: t.color, color: "#fff" }}>
                            {t.name}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                          {t.description ?? "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {t.applies_to_card && <Badge variant="outline">Card</Badge>}
                            {t.applies_to_promotion && <Badge variant="outline">Promo</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {t.is_active ? (
                            <Badge className="bg-green-600">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {t.applies_to_card ? (tagAssignments.cards[t.id]?.length ?? 0) : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {t.applies_to_promotion ? (tagAssignments.promos[t.id]?.length ?? 0) : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {tagAssignments.topups[t.id] ?? 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            {t.applies_to_card && (
                              <Button size="sm" variant="outline" onClick={() => openAssign(t, "card")}>
                                Cards
                              </Button>
                            )}
                            {t.applies_to_promotion && (
                              <Button size="sm" variant="outline" onClick={() => openAssign(t, "promo")}>
                                Promos
                              </Button>
                            )}
                            {t.applies_to_promotion && (
                              <Button size="sm" variant="outline" onClick={() => openBackfill(t)} title="Backfill onto top-ups">
                                <Layers className="h-3 w-3 mr-1" />
                                Backfill
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => openEdit(t)}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteTag(t)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {tags.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                          No tags yet. Click "New Tag" to create one.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assign" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Assignment Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tag</TableHead>
                      <TableHead>Promotions</TableHead>
                      <TableHead className="text-right">Cards Tagged</TableHead>
                      <TableHead className="text-right">Top-ups Tagged</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tags.map((t) => {
                      const promoIds = tagAssignments.promos[t.id] ?? [];
                      const promoNames = promoIds
                        .map((id) => promos.find((p) => p.id === id)?.promo_name)
                        .filter(Boolean);
                      return (
                        <TableRow key={t.id}>
                          <TableCell>
                            <Badge style={{ backgroundColor: t.color, color: "#fff" }}>{t.name}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {promoNames.length === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              promoNames.join(", ")
                            )}
                          </TableCell>
                          <TableCell className="text-right">{tagAssignments.cards[t.id]?.length ?? 0}</TableCell>
                          <TableCell className="text-right">{tagAssignments.topups[t.id] ?? 0}</TableCell>
                        </TableRow>
                      );
                    })}
                    {tags.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                          No tags yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tag CRUD Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTag ? "Edit Tag" : "New Tag"}</DialogTitle>
            <DialogDescription>
              Tags help track where cards were sold or which promotion top-ups occurred under.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Tag Name *</Label>
              <Input value={tagName} onChange={(e) => setTagName(e.target.value)} placeholder="e.g. Summer 2025, Driver Bonus, Online Sale" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={tagDesc} onChange={(e) => setTagDesc(e.target.value)} placeholder="Optional description" />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 ${tagColor === c ? "border-foreground ring-2 ring-foreground/30" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setTagColor(c)}
                  />
                ))}
                <Input
                  type="color"
                  value={tagColor}
                  onChange={(e) => setTagColor(e.target.value)}
                  className="w-12 h-8 p-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Applies To</Label>
              <div className="flex items-center gap-2">
                <Checkbox checked={appliesToCard} onCheckedChange={(v) => setAppliesToCard(v === true)} id="ac" />
                <Label htmlFor="ac" className="font-normal cursor-pointer">Cards (track where a card was sold)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={appliesToPromo} onCheckedChange={(v) => setAppliesToPromo(v === true)} id="ap" />
                <Label htmlFor="ap" className="font-normal cursor-pointer">Promotions (track top-ups under a promo)</Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={tagActive} onCheckedChange={(v) => setTagActive(v === true)} id="aa" />
              <Label htmlFor="aa" className="font-normal cursor-pointer">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={saveTag}>{editingTag ? "Save Changes" : "Create Tag"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Assign tag "{assignTag?.name}" to {assignTargetType === "card" ? "Cards" : "Promotions"}
            </DialogTitle>
            <DialogDescription>
              Check the items you want this tag attached to. Unchecking removes the tag.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder={assignTargetType === "card" ? "Search by last4, card number, or name..." : "Search promo name..."}
              value={searchAssign}
              onChange={(e) => setSearchAssign(e.target.value)}
            />
            <div className="text-xs text-muted-foreground">
              {selectedTargetIds.size} selected of {filteredTargets.length} shown
            </div>
            <ScrollArea className="h-[400px] border rounded-md">
              <div className="p-2 space-y-1">
                {filteredTargets.map((target: any) => {
                  const id = target.id;
                  const checked = selectedTargetIds.has(id);
                  const label = assignTargetType === "promo"
                    ? `${target.promo_name} (${formatDate(target.start_date)} → ${formatDate(target.end_date)})`
                    : `**** ${target.card_last4} — ${target.customer_first_name ?? ""} ${target.customer_last_name ?? ""}`.trim();
                  return (
                    <label
                      key={id}
                      className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          setSelectedTargetIds((prev) => {
                            const next = new Set(prev);
                            if (v === true) next.add(id); else next.delete(id);
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  );
                })}
                {filteredTargets.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No {assignTargetType === "card" ? "cards" : "promotions"} match your search.
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            <Button onClick={saveAssignments}>Save Assignments</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backfill Modal */}
      <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Backfill tag "{backfillTag?.name}" to top-ups</DialogTitle>
            <DialogDescription>
              Select a promotion. This will add the tag to every existing top-up that was processed under that promo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Promotion</Label>
              <Select value={backfillPromoId} onValueChange={(v) => { setBackfillPromoId(v); setBackfillCount(null); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a promotion" />
                </SelectTrigger>
                <SelectContent>
                  {promos.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.promo_name} ({formatDate(p.start_date)} → {formatDate(p.end_date)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={previewBackfill} disabled={!backfillPromoId}>
              Preview top-up count
            </Button>
            {backfillCount !== null && (
              <div className="text-sm bg-muted p-3 rounded">
                Found <strong>{backfillCount}</strong> top-up(s) under this promotion.
                Running backfill will tag all of them with "{backfillTag?.name}".
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBackfillOpen(false)}>Cancel</Button>
            <Button onClick={runBackfill} disabled={!backfillPromoId || backfilling}>
              {backfilling ? "Backfilling..." : "Run Backfill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
